import { ERROR_MESSAGES, LIMITS, type ErrorCode } from '@onerepair/contract';

export type FileStatus = 'queued' | 'uploading' | 'uploaded' | 'failed';
export interface IntakeFile {
  clientId: string;
  fileId?: string;
  name: string;
  sizeBytes: number;
  progress: number; // 0..1
  status: FileStatus;
  error?: string | undefined;
}
export interface IntakeState {
  submissionId?: string;
  files: IntakeFile[];
  email: string;
  submitted: boolean;
  submitting: boolean;
  error: string;
}

export const initialState = (): IntakeState => ({ files: [], email: '', submitted: false, submitting: false, error: '' });

export type Action =
  | { type: 'add'; files: IntakeFile[] }
  | { type: 'issued'; clientId: string; fileId: string }
  | { type: 'progress'; clientId: string; progress: number }
  | { type: 'uploaded'; clientId: string }
  | { type: 'failed'; clientId: string; error: string }
  | { type: 'retry'; clientId: string }
  | { type: 'remove'; clientId: string }
  | { type: 'email'; email: string }
  | { type: 'submission'; submissionId: string }
  | { type: 'submitting' }
  | { type: 'submitted' }
  | { type: 'error'; error: string }
  | { type: 'reset' };

export function reduce(s: IntakeState, a: Action): IntakeState {
  const patch = (clientId: string, p: Partial<IntakeFile>) => ({ ...s, files: s.files.map((f) => (f.clientId === clientId ? { ...f, ...p } : f)) });
  switch (a.type) {
    case 'add': return { ...s, files: [...s.files, ...a.files], error: '' };
    case 'issued': return patch(a.clientId, { fileId: a.fileId, status: 'uploading' });
    case 'progress': return patch(a.clientId, { progress: a.progress, status: 'uploading' });
    case 'uploaded': return patch(a.clientId, { progress: 1, status: 'uploaded' });
    case 'failed': return patch(a.clientId, { status: 'failed', error: a.error });
    case 'retry': return patch(a.clientId, { status: 'queued', progress: 0, error: undefined });
    case 'remove': return { ...s, files: s.files.filter((f) => f.clientId !== a.clientId) };
    case 'email': return { ...s, email: a.email, error: '' };
    case 'submission': return { ...s, submissionId: a.submissionId };
    case 'submitting': return { ...s, submitting: true, error: '' };
    case 'submitted': return { ...s, submitting: false, submitted: true };
    case 'error': return { ...s, submitting: false, error: a.error };
    case 'reset': return initialState();
  }
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Mirrors the API's gate; returns the exact error copy or undefined when submit is allowed. */
export function submitBlocker(s: IntakeState): string | undefined {
  if (!EMAIL_RE.test(s.email.trim())) return ERROR_MESSAGES.INVALID_EMAIL;
  if (s.files.length === 0) return ERROR_MESSAGES.NO_FILES;
  if (s.files.some((f) => f.status !== 'uploaded')) return ERROR_MESSAGES.FILES_NOT_READY;
  return undefined;
}

export function canAdd(s: IntakeState, incoming: number): ErrorCode | undefined {
  if (s.files.length + incoming > LIMITS.maxFilesPerSubmission) return 'TOO_MANY_FILES';
  return undefined;
}
